/**
 * N1 · The ONE details form, split into single-purpose screens.
 *
 * Every validator, every "what's still missing" list and the printed-name
 * derivation live here so the five step screens are VIEWS over one form rather
 * than five implementations. Anything that decides whether a step is complete
 * must be added here, never inline in a screen.
 *
 * DATE OF BIRTH is identity verification (KYC/OFAC). There is deliberately no
 * age comparison in this file — age screening is the separate S3 affirmation and
 * its own consent record. Do not merge the two.
 */
import { addressMissingFields, isAddressComplete, type AddressDraft } from "@/lib/postal-address";

export type DetailsStep = "name" | "phone" | "dob" | "address";

/**
 * Flow order. The load screen and the review screen follow the last entry.
 *
 * The fee-only screen is GONE: its sole job was stating what Cardmoji charges,
 * which the review screen states again with the figure and the load separation
 * ("Cardmoji card fee", "Total due to Cardmoji", s7.order.loadNote). The
 * `details` funnel event it used to emit now fires from the LAST step here, so
 * the stage is still recorded.
 */
export const DETAILS_STEPS: DetailsStep[] = ["name", "phone", "dob", "address"];


export function nextDetailsStep(step: DetailsStep): string {
  const i = DETAILS_STEPS.indexOf(step);
  return i === DETAILS_STEPS.length - 1 ? "../load" : `../${DETAILS_STEPS[i + 1]}`;
}

export function prevDetailsStep(step: DetailsStep): string {
  const i = DETAILS_STEPS.indexOf(step);
  return i <= 0 ? "../reveal" : `../${DETAILS_STEPS[i - 1]}`;
}

/* ------------------------------------------------------------------ name */

export function printedName(first: string, middle: string, last: string): string {
  const mid = middle.trim() ? `${middle.trim().charAt(0).toUpperCase()} ` : "";
  const full = `${first.trim()} ${mid}${last.trim()}`.replace(/\s+/g, " ").trim();
  return full ? full.toUpperCase().slice(0, 21) : "—";
}

export function nameMissing(first: string, last: string): string[] {
  const missing: string[] = [];
  if (!first.trim()) missing.push("First name");
  if (!last.trim()) missing.push("Last name");
  return missing;
}

/* ----------------------------------------------------------------- phone */

export const phoneDigitsOf = (v: string) => v.replace(/\D/g, "");
export const phoneIsValid = (v: string) => phoneDigitsOf(v).length === 10;

export function phoneMissing(v: string): string[] {
  const digits = phoneDigitsOf(v);
  if (!digits) return ["Mobile number"];
  if (digits.length !== 10) return ["Mobile number — 10 digits"];
  return [];
}

/* ------------------------------------------------------------------- dob */

export type DobState = "empty" | "incomplete" | "invalid" | "future" | "valid";

export function parseDob(month: string, day: string, year: string): { iso: string | null; state: DobState } {
  const y = year.trim(), m = month.trim(), d = day.trim();
  if (!y && !m && !d) return { iso: null, state: "empty" };
  if (y.length !== 4 || m.length < 1 || d.length < 1) return { iso: null, state: "incomplete" };
  const yi = Number(y), mi = Number(m), di = Number(d);
  const dt = new Date(Date.UTC(yi, mi - 1, di));
  const real = dt.getUTCFullYear() === yi && dt.getUTCMonth() === mi - 1 && dt.getUTCDate() === di;
  if (!real) return { iso: null, state: "invalid" };
  if (dt.getTime() > Date.now()) return { iso: null, state: "future" };
  // Typo guard on the YEAR field only — not an age rule.
  if (yi < 1900) return { iso: null, state: "invalid" };
  const iso = `${String(yi).padStart(4, "0")}-${String(mi).padStart(2, "0")}-${String(di).padStart(2, "0")}`;
  return { iso, state: "valid" };
}

export function dobMissing(state: DobState): string[] {
  switch (state) {
    case "empty": return ["Date of birth"];
    case "incomplete": return ["Date of birth — month, day and year"];
    case "future": return ["Date of birth — can't be in the future"];
    case "invalid": return ["Date of birth — not a real date"];
    default: return [];
  }
}

/* --------------------------------------------------------------- address */

export function panMatches(pan: string, digits: number): boolean {
  if (digits <= 0) return true;
  return new RegExp(`^\\d{${digits}}$`).test(pan.trim());
}

export function addressStepMissing(args: {
  deliveryType: string;
  branchId: string;
  address: AddressDraft;
  pan: string;
  panDigits: number;
}): string[] {
  const missing: string[] = [];
  if (args.deliveryType === "mail") missing.push(...addressMissingFields(args.address));
  if (args.panDigits > 0 && !panMatches(args.pan, args.panDigits)) {
    missing.push(`Card number — last ${args.panDigits} digits`);
  }
  return missing;
}

/* ------------------------------------------- where should the customer be? */

/**
 * The first step whose data is not yet staged. Used by every step screen to
 * bounce a customer who deep-links past a gate, so the sequence can never be
 * skipped and can never dead-end.
 */
export function firstIncompleteStep(
  d: Record<string, any> | null | undefined,
  panDigits: number,
): DetailsStep | null {
  if (!d) return "name";
  if (nameMissing(d.first_name ?? "", d.last_name ?? "").length) return "name";
  if (phoneMissing(d.phone ?? "").length) return "phone";
  if (dobMissing(parseDob(
    (d.date_of_birth ?? "").slice(5, 7),
    (d.date_of_birth ?? "").slice(8, 10),
    (d.date_of_birth ?? "").slice(0, 4),
  ).state).length) return "dob";
  if (!d.delivery_type) return "address";
  if (d.delivery_type === "mail" && !isAddressComplete(d.postal_address ?? {})) return "address";
  if (panDigits > 0 && !panMatches(d.pan_last5 ?? "", panDigits)) return "address";
  return null;
}
